/**
 * Query String Utility
 *
 * Provides helper functions for building URL query strings from objects.
 */

/**
 * Builds a URL query string from an options object
 *
 * @param options - Object with key-value pairs to convert to query parameters
 * @returns Query string with leading "?" if parameters exist, empty string otherwise
 *
 * @example
 * ```typescript
 * buildQueryString({ page: 1, limit: 10 })
 * // Returns: "?page=1&limit=10"
 *
 * buildQueryString({ filter: 'active', sortBy: 'name', empty: undefined })
 * // Returns: "?filter=active&sortBy=name" (undefined/null values are excluded)
 *
 * buildQueryString({})
 * // Returns: ""
 * ```
 */
export function buildQueryString(options: Record<string, any>): string {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Builds a URL query string from an options object without the leading "?"
 * Useful when you need to append to an existing query string or build custom URLs
 *
 * @param options - Object with key-value pairs to convert to query parameters
 * @returns Query string without leading "?", empty string if no parameters
 *
 * @example
 * ```typescript
 * buildQueryStringRaw({ page: 1, limit: 10 })
 * // Returns: "page=1&limit=10"
 *
 * const baseUrl = '/api/search?query=test';
 * const additionalParams = buildQueryStringRaw({ page: 2 });
 * const fullUrl = `${baseUrl}&${additionalParams}`;
 * // Results in: "/api/search?query=test&page=2"
 * ```
 */
export function buildQueryStringRaw(options: Record<string, any>): string {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  return params.toString();
}

/**
 * Parses a query string into an object
 *
 * @param queryString - Query string to parse (with or without leading "?")
 * @returns Object with parsed key-value pairs
 *
 * @example
 * ```typescript
 * parseQueryString("?page=1&limit=10")
 * // Returns: { page: "1", limit: "10" }
 *
 * parseQueryString("page=1&limit=10")
 * // Returns: { page: "1", limit: "10" }
 *
 * parseQueryString("")
 * // Returns: {}
 * ```
 */
export function parseQueryString(queryString: string): Record<string, string> {
  const cleanString = queryString.startsWith('?')
    ? queryString.slice(1)
    : queryString;

  if (!cleanString) {
    return {};
  }

  const params = new URLSearchParams(cleanString);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

/**
 * Merges multiple query parameter objects into a single query string
 * Later objects override earlier ones for duplicate keys
 *
 * @param optionsList - Array of option objects to merge
 * @returns Query string with leading "?" if parameters exist
 *
 * @example
 * ```typescript
 * mergeQueryStrings(
 *   { page: 1, limit: 10 },
 *   { page: 2 }, // This overrides page: 1
 *   { sort: 'name' }
 * )
 * // Returns: "?page=2&limit=10&sort=name"
 * ```
 */
export function mergeQueryStrings(
  ...optionsList: Record<string, any>[]
): string {
  const merged = Object.assign({}, ...optionsList);
  return buildQueryString(merged);
}
